import React, { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import type { AuthClient } from "../authClient";
import {
  CaptchaVerificationFailedError,
  CaptchaVerificationRequiredError,
  EmailAlreadyExistsError,
} from "../errors";
import { validateEmail } from "../../utils";
import { AuthPage } from "./AuthPage";

const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as
  | string
  | undefined;

export interface AuthRegisterProps {
  appTitle: string;
  logo: string;
  authClient: AuthClient;
  /** Called once with the `?ref=` / `?invite=` query params found on mount, e.g. to persist a referral. */
  onReferralParams?: (params: { ref?: string; invite?: string }) => void;
}

export function Register({
  appTitle,
  logo,
  authClient,
  onReferralParams,
}: AuthRegisterProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const referralHandled = useRef(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (referralHandled.current || !onReferralParams) return;
    const ref = searchParams.get("ref") ?? undefined;
    const invite = searchParams.get("invite") ?? undefined;
    if (ref || invite) {
      onReferralParams({ ref, invite });
    }
    referralHandled.current = true;
  }, [searchParams, onReferralParams]);

  const turnstileRef = useRef<TurnstileInstance>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [captchaWidgetError, setCaptchaWidgetError] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [showPassword, setShowPassword] = useState(false);
  const isTurnstileEnabled = Boolean(turnstileSiteKey);

  const resetTurnstile = useCallback(() => {
    setTurnstileToken("");
    setCaptchaWidgetError("");
    turnstileRef.current?.reset();
  }, []);

  const handleTurnstileSuccess = useCallback((token: string) => {
    setTurnstileToken(token);
    setCaptchaWidgetError("");
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken("");
  }, []);

  const handleTurnstileError = useCallback(() => {
    setTurnstileToken("");
    setCaptchaWidgetError(t("CaptchaFailed"));
  }, [t]);

  const validatePasswordStrength = (pwd: string): string => {
    const passwordRegex =
      /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%?*/.^&_()-])[A-Za-z\d!@#$%?*/.^&_()-]{10,50}$/;
    if (!passwordRegex.test(pwd)) {
      return t("PasswordComplexity");
    }
    return "";
  };

  const validateField = (
    field: "email" | "password" | "confirmPassword",
    value: string,
  ): string => {
    if (field === "email" && value && !validateEmail(value)) {
      return t("EmailValid");
    }
    if (field === "password" && value) {
      return validatePasswordStrength(value);
    }
    if (
      field === "confirmPassword" &&
      value &&
      password &&
      value !== password
    ) {
      return t("PasswordMatch");
    }
    return "";
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!validateEmail(email)) {
      errors.email = t("EmailValid");
    }

    if (password) {
      const pwdError = validatePasswordStrength(password);
      if (pwdError) {
        errors.password = pwdError;
      }
    }

    if (password && confirmPassword && password !== confirmPassword) {
      errors.confirmPassword = t("PasswordMatch");
    }

    if (isTurnstileEnabled && !turnstileToken) {
      setError(t("CaptchaRequired"));
    }

    setValidationErrors(errors);
    return (
      Object.keys(errors).length === 0 &&
      (!isTurnstileEnabled || Boolean(turnstileToken))
    );
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    setValidationErrors((prev) => ({
      ...prev,
      email: validateField("email", value),
    }));
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    setValidationErrors((prev) => ({
      ...prev,
      password: validateField("password", value),
    }));
  };

  const handleConfirmPasswordChange = (value: string) => {
    setConfirmPassword(value);
    setValidationErrors((prev) => ({
      ...prev,
      confirmPassword: validateField("confirmPassword", value),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await authClient.register(
        firstName,
        lastName,
        email,
        password,
        confirmPassword,
        turnstileToken || undefined,
      );
      navigate("/login", {
        state: { message: t("RegistrationSuccessful") },
        replace: true,
      });
    } catch (err) {
      if (isTurnstileEnabled) {
        resetTurnstile();
      }

      if (err instanceof EmailAlreadyExistsError) {
        setError(t("EmailAlreadyExists"));
      } else if (err instanceof CaptchaVerificationRequiredError) {
        setError(t("CaptchaRequired"));
      } else if (err instanceof CaptchaVerificationFailedError) {
        setError(t("CaptchaFailed"));
      } else {
        setError(t("RegistrationFailed"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPage appTitle={appTitle} logo={logo}>
      <Typography
        component="h2"
        variant="body1"
        sx={{ mb: 1, color: "text.secondary" }}
      >
        {t("Register")}
      </Typography>
      <Typography variant="body2" sx={{ mb: 4, color: "text.secondary" }}>
        {t("CreateNewAccount")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ width: "100%", mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
        <TextField
          margin="normal"
          required
          fullWidth
          id="firstName"
          label={t("FirstName")}
          name="firstName"
          autoComplete="given-name"
          autoFocus
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          id="lastName"
          label={t("LastName")}
          name="lastName"
          autoComplete="family-name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          id="email"
          label={t("Email")}
          name="email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => handleEmailChange(e.target.value)}
          error={!!validationErrors.email}
          helperText={validationErrors.email}
          sx={{ mb: 2 }}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          name="password"
          label={t("Password")}
          type={showPassword ? "text" : "password"}
          id="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => handlePasswordChange(e.target.value)}
          error={!!validationErrors.password}
          helperText={validationErrors.password || t("PasswordRequirements")}
          sx={{ mb: 2 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          name="confirmPassword"
          label={t("ConfirmPassword")}
          type={showPassword ? "text" : "password"}
          id="confirmPassword"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(e) => handleConfirmPasswordChange(e.target.value)}
          error={!!validationErrors.confirmPassword}
          helperText={validationErrors.confirmPassword}
          sx={{ mb: isTurnstileEnabled ? 2 : 3 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {turnstileSiteKey && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Turnstile
                ref={turnstileRef}
                siteKey={turnstileSiteKey}
                onSuccess={handleTurnstileSuccess}
                onExpire={handleTurnstileExpire}
                onError={handleTurnstileError}
                options={{
                  theme: "auto",
                  retry: "never",
                  refreshExpired: "manual",
                }}
              />
            </Box>
            {captchaWidgetError && (
              <Box sx={{ mt: 1, textAlign: "center" }}>
                <Typography variant="body2" color="error">
                  {captchaWidgetError}
                </Typography>
                <Button
                  type="button"
                  size="small"
                  onClick={resetTurnstile}
                  sx={{ mt: 0.5 }}
                >
                  {t("CaptchaRetry")}
                </Button>
              </Box>
            )}
          </Box>
        )}
        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={
            loading ||
            !firstName.trim() ||
            !email.trim() ||
            !password.trim() ||
            !confirmPassword.trim() ||
            (isTurnstileEnabled && !turnstileToken)
          }
          sx={{ py: 1.5, fontSize: "1rem", fontWeight: 600, mb: 2 }}
        >
          {loading ? t("Registering") : t("Register")}
        </Button>
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            {t("AlreadyHaveAnAccount")}{" "}
            <Link
              to="/login"
              style={{
                color: "inherit",
                textDecoration: "none",
                fontWeight: 600,
              }}
            >
              {t("Login")}
            </Link>
          </Typography>
        </Box>
      </Box>
    </AuthPage>
  );
}
